<template>
  <b-modal
    :active.sync="isModalActive"
    has-modal-card
    class="is-wider-modal"
    :can-cancel="['escape']"
  >
    <div class="modal-card">
      <header class="modal-card-head">
        <h1 class="is-title-header modal-card-title">Add Products</h1>
        <div
          class="header-action-buttons"
          v-if="activeTab > 0 && visibleStreams[activeTab]"
        >
          <b-button
            type="is-primary"
            size="is-small"
            icon-left="plus"
            @click="addStreamInstance(visibleStreams[activeTab].key)"
          >
            Add
          </b-button>
          <b-button
            v-if="getStreamInstances(visibleStreams[activeTab].key).length > 1"
            type="is-danger"
            size="is-small"
            icon-left="delete"
            @click="removeCurrentInstance()"
          >
            Remove
          </b-button>
        </div>
      </header>
      <section class="modal-card-body scrollable-modal-body">
        <b-tabs v-model="activeTab" type="is-boxed" class="sticky-tabs-wrapper">
          <b-tab-item
            v-for="stream in visibleStreams"
            :key="stream.key"
            :label="stream.label"
          >
            <!-- For PRODUCT SHEET (single instance) -->
            <template v-if="stream.key === 'PRODUCT SHEET'">
              <div class="tab-content-wrapper">
                <form-field
                  v-for="field in getFieldsForStream(stream.name)"
                  :key="`${stream.key}-${field.product_data_column}`"
                  :field="field"
                  :formData="
                    tabFormData[stream.key]
                      ? tabFormData[stream.key].fields
                      : {}
                  "
                  :dropdownOptions="getDropdownOptionsFromConfig(field)"
                  :validationError="
                    getValidationError(stream.key, field.product_data_column)
                  "
                  @input="handleFieldInput(stream.key, field, $event)"
                  @dropdown-change="
                    checkDependency(
                      stream.key,
                      $event,
                      field.product_data_column
                    )
                  "
                />
              </div>
            </template>

            <!-- For streams that support multiples (BATT, PACK, WEEE, BUNDLE) -->
            <template v-else>
              <div class="tab-content-wrapper">
                <b-tabs
                  v-model="stream.activeSubTab"
                  type="is-toggle"
                  size="is-small"
                  class="sub-tabs-inline"
                >
                  <b-tab-item
                    v-for="(instance, index) in getStreamInstances(stream.key)"
                    :key="instance.id"
                    :label="`${stream.label} ${index + 1}`"
                  >
                    <form-field
                      v-for="field in getFieldsForStream(stream.name)"
                      :key="`${instance.id}-${field.product_data_column}`"
                      :field="field"
                      :formData="instance.fields"
                      :dropdownOptions="getDropdownOptionsFromConfig(field)"
                      :validationError="
                        getValidationError(
                          instance.id,
                          field.product_data_column
                        )
                      "
                      @input="handleFieldInput(instance.id, field, $event)"
                      @dropdown-change="
                        checkDependency(
                          instance.id,
                          $event,
                          field.product_data_column
                        )
                      "
                    />
                  </b-tab-item>
                </b-tabs>
              </div>
            </template>
          </b-tab-item>
        </b-tabs>
      </section>
      <footer class="modal-card-foot">
        <b-button
          type="is-primary"
          @click="saveProduct"
          :loading="isSaving"
          :disabled="!isFormValid"
        >
          Save
        </b-button>
        <b-button type="is-danger" @click="cancel">Cancel</b-button>
      </footer>
    </div>
  </b-modal>
</template>

<script>
import * as dayjs from "dayjs";
import FormField from "./FormField.vue";

const STREAM_CONFIG = [
  {
    key: "PRODUCT SHEET",
    name: "PRODUCT SHEET",
    label: "Product Sheet",
    multiple: false,
    activeSubTab: 0
  },
  {
    key: "BATT",
    name: "BATT",
    label: "Batteries",
    multiple: true,
    activeSubTab: 0
  },
  {
    key: "PACK",
    name: "PACK",
    label: "Packaging",
    multiple: true,
    activeSubTab: 0
  },
  { key: "WEEE", name: "WEEE", label: "WEEE", multiple: true, activeSubTab: 0 },
  {
    key: "BUNDLE",
    name: "BUNDLE INFORMATION",
    label: "Bundle",
    multiple: true,
    activeSubTab: 0
  }
];

const DROPDOWN_FIELDS = [
  "product_group",
  "product_family",
  "material_general",
  "material_detail",
  "chemistry",
  "packaging_type",
  "packaging_level"
];

const DEPENDENT_FIELDS = {
  product_group: {
    child: "product_family",
    resetFields: ["product_family"]
  },
  material_general: {
    child: "material_detail",
    resetFields: ["material_detail"]
  }
};

export default {
  name: "AddProduct",
  components: {
    FormField
  },
  props: {
    isActive: {
      type: Boolean,
      default: false
    },
    customerId: {
      type: Number,
      required: true
    },
    cdfProductSheet: {
      type: Array,
      required: true
    },
    productFamilyDropDown: {
      type: Array,
      default: () => []
    },
    productGroupDropDown: {
      type: Array,
      default: () => []
    },
    materialDetailDropDown: {
      type: Array,
      default: () => []
    },
    materialGeneralDropdown: {
      type: Array,
      default: () => []
    },
    packagingTypeDropDown: {
      type: Array,
      default: () => []
    },
    packagingLevelDropDown: {
      type: Array,
      default: () => []
    },
    chemistryDropDown: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      isModalActive: false,
      activeTab: 0,
      tabFormData: {},
      streamInstances: {},
      instanceCounter: 0,
      validationErrors: {},
      isSaving: false,
      allFields: [],
      reactivityTrigger: 0,
      dropdownData: {
        product_group: [],
        product_family: [],
        material_detail: [],
        material_general: [],
        chemistry: [],
        packaging_type: [],
        packaging_level: []
      }
    };
  },
  computed: {
    visibleStreams() {
      this.reactivityTrigger;

      return STREAM_CONFIG.filter(stream => {
        if (stream.key === "PRODUCT SHEET") return true;

        const productSheetFields = this.tabFormData["PRODUCT SHEET"]?.fields;
        if (!productSheetFields) return false;

        const streamKeyLower = stream.key.toLowerCase();
        const fieldValue = productSheetFields[streamKeyLower];

        // Show the stream if "Yes" is selected
        if (this.isYesValue(fieldValue)) {
          // Check if there are fields in template OR if there's data in streamInstances
          const hasTemplateFields =
            this.getFieldsForStream(stream.name).length > 0;
          const hasStreamData = this.streamInstances[stream.key]?.length > 0;
          return hasTemplateFields || hasStreamData;
        }

        return false;
      });
    },
    isFormValid() {
      if (!this.isModalActive) {
        return true;
      }

      for (const stream of this.visibleStreams) {
        const streamFields = this.getFieldsForStream(stream.name);
        const requiredFields = streamFields.filter(f => f.mandatory);

        if (stream.key === "PRODUCT SHEET") {
          const productSheetData = this.tabFormData[stream.key];
          if (!productSheetData || !productSheetData.fields) {
            return false;
          }

          const hasAllRequired = requiredFields.every(
            field => productSheetData.fields[field.product_data_column]
          );
          const hasErrors =
            Object.keys(this.validationErrors[stream.key] || {}).length > 0;
          if (!hasAllRequired || hasErrors) return false;
        } else {
          // Check all instances of multiple streams
          const instances = this.getStreamInstances(stream.key);
          if (!instances.length) return false;

          for (const instance of instances) {
            if (!instance || !instance.fields) {
              return false;
            }
            const hasAllRequired = requiredFields.every(
              field => instance.fields[field.product_data_column]
            );
            const hasErrors =
              Object.keys(this.validationErrors[instance.id] || {}).length > 0;
            if (!hasAllRequired || hasErrors) return false;
          }
        }
      }
      return true;
    }
  },
  watch: {
    isActive(newValue) {
      this.isModalActive = newValue;
      if (newValue) {
        this.initializeForm();
      }
    },
    isModalActive(newValue) {
      if (!newValue) {
        this.$emit("close");
      }
    },
    activeTab() {
      this.$nextTick(() => {
        const modalBody = this.$el.querySelector(".scrollable-modal-body");
        if (modalBody) {
          modalBody.scrollTop = 0;
        }
      });
    },
    productGroupDropDown(newVal) {
      if (newVal && newVal.length > 0) {
        this.$set(this.dropdownData, "product_group", newVal);
      }
    },
    productFamilyDropDown(newVal) {
      if (newVal && newVal.length > 0) {
        this.$set(this.dropdownData, "product_family", newVal);
      }
    },
    materialGeneralDropdown(newVal) {
      if (newVal && newVal.length > 0) {
        this.$set(this.dropdownData, "material_general", newVal);
      }
    },
    materialDetailDropDown(newVal) {
      if (newVal && newVal.length > 0) {
        this.$set(this.dropdownData, "material_detail", newVal);
      }
    },
    chemistryDropDown(newVal) {
      if (newVal && newVal.length > 0) {
        this.$set(this.dropdownData, "chemistry", newVal);
      }
    },
    packagingTypeDropDown(newVal) {
      if (newVal && newVal.length > 0) {
        this.$set(this.dropdownData, "packaging_type", newVal);
      }
    },
    packagingLevelDropDown(newVal) {
      if (newVal && newVal.length > 0) {
        this.$set(this.dropdownData, "packaging_level", newVal);
      }
    }
  },
  methods: {
    isYesValue(value) {
      if (!value) return false;
      // Handle string values
      if (typeof value === "string") {
        return value.toLowerCase() === "yes";
      }
      // Handle object values
      if (typeof value === "object" && value.name) {
        return value.name.toLowerCase() === "yes";
      }
      return false;
    },

    getStreamInstances(streamKey) {
      return this.streamInstances[streamKey] || [];
    },

    addStreamInstance(streamKey) {
      const stream = STREAM_CONFIG.find(s => s.key === streamKey);
      if (!stream) return;

      const instanceId = `${streamKey}_${++this.instanceCounter}`;
      const newInstance = {
        id: instanceId,
        stream_name: stream.name,
        stream_key: stream.key,
        external_comment: "",
        fields: {}
      };

      if (!this.streamInstances[streamKey]) {
        this.$set(this.streamInstances, streamKey, []);
      }
      this.streamInstances[streamKey].push(newInstance);
      this.$set(this.validationErrors, instanceId, {});

      // Switch to the newly added tab
      stream.activeSubTab = this.streamInstances[streamKey].length - 1;
    },

    removeStreamInstance(streamKey, activeTabIndex) {
      const instances = this.streamInstances[streamKey];
      if (instances.length <= 1) return; // Don't remove if only one left

      const instanceToRemove = instances[activeTabIndex];
      if (instanceToRemove) {
        instances.splice(activeTabIndex, 1);
        this.$delete(this.validationErrors, instanceToRemove.id);

        // Update active tab to stay on a valid index
        const stream = STREAM_CONFIG.find(s => s.key === streamKey);
        if (stream) {
          // If we removed the last tab, go to the new last tab
          if (activeTabIndex >= instances.length) {
            stream.activeSubTab = instances.length - 1;
          }
          // Otherwise stay on the same index (which now shows the next form)
        }
      }
    },

    removeCurrentInstance() {
      const currentStream = this.visibleStreams[this.activeTab];
      if (!currentStream) return;

      const streamKey = currentStream.key;
      const activeSubTab = currentStream.activeSubTab;

      this.removeStreamInstance(streamKey, activeSubTab);
    },

    findInstanceById(instanceId) {
      for (const streamKey in this.streamInstances) {
        const found = this.streamInstances[streamKey].find(
          inst => inst.id === instanceId
        );
        if (found) return found;
      }
      return null;
    },

    initializeForm(isAddMode = true) {
      this.tabFormData = {};
      this.streamInstances = {};
      this.validationErrors = {};
      this.instanceCounter = 0;
      this.activeTab = 0;

      // Get template fields and deduplicate by product_data_column
      // When duplicates exist, merge their dropdown values
      const templateFields = this.cdfProductSheet[0]?.template_fields || [];
      const fieldMap = new Map();

      templateFields.forEach(field => {
        const columnName = field.product_data_column;
        if (fieldMap.has(columnName)) {
          // Merge dropdown values from duplicate fields
          const existingField = fieldMap.get(columnName);
          if (
            field.dropdown_field_values &&
            Array.isArray(field.dropdown_field_values)
          ) {
            if (!existingField.dropdown_field_values) {
              existingField.dropdown_field_values = [];
            }
            // Merge and deduplicate dropdown values
            const existingValues = new Set(existingField.dropdown_field_values);
            field.dropdown_field_values.forEach(val => existingValues.add(val));
            existingField.dropdown_field_values = Array.from(existingValues);
          }
        } else {
          // First occurrence - add to map
          fieldMap.set(columnName, { ...field });
        }
      });

      this.allFields = Array.from(fieldMap.values());

      // Make product_name mandatory
      const productNameField = this.allFields.find(
        f => f.product_data_column === "product_name"
      );
      if (productNameField) {
        productNameField.mandatory = true;
      }

      // Initialize form data for PRODUCT SHEET
      STREAM_CONFIG.forEach(stream => {
        if (stream.key === "PRODUCT SHEET") {
          this.tabFormData[stream.key] = {
            stream_name: stream.name,
            stream_key: stream.key,
            external_comment: "",
            fields: {}
          };
          this.validationErrors[stream.key] = {};
        } else {
          // Initialize empty array for multiple streams
          this.streamInstances[stream.key] = [];

          // For "add new" mode, create one default instance per stream
          // For "edit" mode, don't create defaults - they'll be populated from response
          if (isAddMode) {
            const instanceId = `${stream.key}_${++this.instanceCounter}`;
            const instance = {
              id: instanceId,
              stream_name: stream.name,
              stream_key: stream.key,
              external_comment: "",
              fields: {}
            };
            this.streamInstances[stream.key].push(instance);
            this.validationErrors[instanceId] = {};
          }
        }
      });

      this.initializeDropdowns();
    },

    initializeDropdowns() {
      DROPDOWN_FIELDS.forEach(fieldName => {
        const propName = this.getDropdownPropName(fieldName);
        // First try to use prop data from parent
        if (propName && this[propName] && this[propName].length > 0) {
          this.dropdownData[fieldName] = this[propName];
        } else {
          // Fallback to processing from template fields
          const fieldConfig = this.getFieldConfig(fieldName);
          if (fieldConfig) {
            this.dropdownData[fieldName] = this.processDropdownValues(
              fieldConfig.dropdown_field_values
            );
          }
        }
      });
    },

    getDropdownPropName(fieldName) {
      const propMap = {
        product_group: "productGroupDropDown",
        product_family: "productFamilyDropDown",
        material_general: "materialGeneralDropdown",
        material_detail: "materialDetailDropDown",
        chemistry: "chemistryDropDown",
        packaging_type: "packagingTypeDropDown",
        packaging_level: "packagingLevelDropDown"
      };
      return propMap[fieldName];
    },

    processDropdownValues(values) {
      if (!Array.isArray(values)) return [];
      return values
        .map(element => ({ name: element }))
        .filter(item => item.name && item.name !== "NA");
    },

    getFieldConfig(columnName) {
      return this.allFields.find(
        col => col.product_data_column?.trim() === columnName.trim()
      );
    },

    getFieldsForStream(streamName) {
      if (!this.allFields.length) return [];

      // Fields that should only appear in PRODUCT SHEET
      const productSheetOnlyFields = [
        "ean",
        "product_number",
        "product_model",
        "product_name",
        "mhra_pl_number",
        "weight_mpn",
        "pip_code",
        "itf_code",
        "supplier_name",
        "supplier_number"
      ];

      return this.allFields.filter(field => {
        if (!field.field_required || field.sheet_name !== streamName) {
          return false;
        }

        // If not PRODUCT SHEET, exclude product-sheet-only fields
        if (streamName !== "PRODUCT SHEET") {
          const columnName = field.product_data_column?.toLowerCase();
          if (productSheetOnlyFields.includes(columnName)) {
            return false;
          }
        }

        return true;
      });
    },

    getDropdownOptionsFromConfig(field) {
      const columnName = field.product_data_column;

      // Return cached dropdown data if available and has values
      if (this.dropdownData[columnName]?.length > 0) {
        return this.dropdownData[columnName];
      }

      // Handle dropdown from field configuration
      if (field.dropdown_field_values?.length > 0) {
        return this.processDropdownValues(field.dropdown_field_values);
      }

      return [];
    },

    checkDependency(streamKeyOrInstanceId, selectedItem, fieldName) {
      const dependency = DEPENDENT_FIELDS[fieldName];
      if (!dependency) return;

      // Reset dependent fields
      dependency.resetFields.forEach(resetField => {
        this.dropdownData[resetField] = [];

        if (streamKeyOrInstanceId === "PRODUCT SHEET") {
          this.tabFormData[streamKeyOrInstanceId].fields[resetField] = null;
        } else {
          const instance = this.findInstanceById(streamKeyOrInstanceId);
          if (instance) {
            instance.fields[resetField] = null;
          }
        }
      });

      // Load child dropdown options
      const childFieldConfig = this.getFieldConfig(dependency.child);
      const selectedName =
        (selectedItem && selectedItem.name) ||
        (selectedItem && selectedItem.value) ||
        selectedItem;

      if (
        !childFieldConfig ||
        !selectedName ||
        typeof selectedName !== "string"
      ) {
        return;
      }

      const dropdownValues = childFieldConfig.dropdown_field_values;

      // Normalize possible dropdown structures
      // 1) Array with a single mapping object [{ Parent: [children] }]
      // 2) Plain object map { Parent: [children] }
      // 3) Already an array of child values
      if (Array.isArray(dropdownValues)) {
        const mapping = dropdownValues.find(
          entry => entry && typeof entry === "object" && !Array.isArray(entry)
        );

        if (mapping && mapping[selectedName]) {
          this.dropdownData[dependency.child] = this.processDropdownValues(
            mapping[selectedName]
          );
        } else if (mapping && mapping[selectedName.trim()]) {
          this.dropdownData[dependency.child] = this.processDropdownValues(
            mapping[selectedName.trim()]
          );
        } else if (!mapping) {
          this.dropdownData[dependency.child] = this.processDropdownValues(
            dropdownValues
          );
        }
      } else if (
        dropdownValues &&
        typeof dropdownValues === "object" &&
        dropdownValues[selectedName]
      ) {
        this.dropdownData[dependency.child] = this.processDropdownValues(
          dropdownValues[selectedName]
        );
      } else if (
        dropdownValues &&
        typeof dropdownValues === "object" &&
        dropdownValues[selectedName.trim()]
      ) {
        this.dropdownData[dependency.child] = this.processDropdownValues(
          dropdownValues[selectedName.trim()]
        );
      }
    },

    handleFieldInput(streamKeyOrInstanceId, field, value) {
      // Check if it's PRODUCT SHEET or an instance ID
      if (streamKeyOrInstanceId === "PRODUCT SHEET") {
        this.$set(
          this.tabFormData[streamKeyOrInstanceId].fields,
          field.product_data_column,
          value
        );
        this.reactivityTrigger++;
      } else {
        // It's an instance ID for multiple streams
        const instance = this.findInstanceById(streamKeyOrInstanceId);
        if (instance) {
          this.$set(instance.fields, field.product_data_column, value);
        }
      }

      this.validateField(streamKeyOrInstanceId, field);
    },

    validateField(streamKeyOrInstanceId, field) {
      let value;
      if (streamKeyOrInstanceId === "PRODUCT SHEET") {
        value = this.tabFormData[streamKeyOrInstanceId].fields[
          field.product_data_column
        ];
      } else {
        const instance = this.findInstanceById(streamKeyOrInstanceId);
        value = instance ? instance.fields[field.product_data_column] : null;
      }

      const fieldName = field.product_data_column;
      let errorMessage = null;

      if (field.mandatory && !value) {
        errorMessage = `${field.field_name} is required`;
      } else if (
        field.field_type === "Text" &&
        value &&
        field.max_length &&
        value.length > field.max_length
      ) {
        errorMessage = `Maximum ${field.max_length} characters allowed`;
      } else if (
        (field.field_type === "Number" ||
          field.field_type === "Number-range") &&
        value
      ) {
        if (isNaN(value)) {
          errorMessage = "Must be a valid number";
        } else if (field.min_value && value < field.min_value) {
          errorMessage = `Minimum value is ${field.min_value}`;
        } else if (field.max_value && value > field.max_value) {
          errorMessage = `Maximum value is ${field.max_value}`;
        }
      }

      if (errorMessage) {
        this.$set(
          this.validationErrors[streamKeyOrInstanceId],
          fieldName,
          errorMessage
        );
      } else {
        this.$delete(this.validationErrors[streamKeyOrInstanceId], fieldName);
      }
    },

    getValidationError(streamKeyOrInstanceId, fieldName) {
      return this.validationErrors[streamKeyOrInstanceId]?.[fieldName] || "";
    },

    async saveProduct() {
      const streamWithoutInstance = this.visibleStreams.find(
        stream =>
          stream.key !== "PRODUCT SHEET" &&
          this.getStreamInstances(stream.key).length === 0
      );

      if (streamWithoutInstance) {
        const streamIndex = this.visibleStreams.findIndex(
          stream => stream.key === streamWithoutInstance.key
        );

        this.addStreamInstance(streamWithoutInstance.key);
        if (streamIndex >= 0) {
          this.activeTab = streamIndex;
        }

        this.$buefy.toast.open({
          message: `Please complete required fields in ${streamWithoutInstance.label}`,
          type: "is-danger"
        });
        return;
      }

      // Validate all fields in all visible tabs before saving
      this.visibleStreams.forEach(stream => {
        const streamFields = this.getFieldsForStream(stream.name);
        if (stream.key === "PRODUCT SHEET") {
          streamFields.forEach(field => this.validateField(stream.key, field));
        } else {
          // Validate all instances
          this.getStreamInstances(stream.key).forEach(instance => {
            streamFields.forEach(field =>
              this.validateField(instance.id, field)
            );
          });
        }
      });

      if (!this.isFormValid) {
        this.$buefy.toast.open({
          message: "Please fill all required fields correctly",
          type: "is-danger"
        });
        return;
      }

      this.isSaving = true;

      try {
        const productData = this.prepareProductData();
        const duplicateCheck = await this.checkForDuplicates(productData);

        if (duplicateCheck.isDuplicate) {
          this.$buefy.dialog.confirm({
            title: "Duplicate Product",
            message: `A product with similar details already exists. Do you want to continue?`,
            confirmText: "Continue",
            cancelText: "Cancel",
            type: "is-warning",
            hasIcon: true,
            onConfirm: () => this.submitProduct(productData)
          });
        } else {
          await this.submitProduct(productData);
        }
      } catch (error) {
        this.handleError(error);
      }
    },

    prepareProductData() {
      const formattedData = {};

      this.visibleStreams.forEach(stream => {
        if (stream.key === "PRODUCT SHEET") {
          const tabData = this.tabFormData[stream.key];
          const processedFields = this.processFieldValues(tabData.fields);

          if (
            Object.keys(processedFields).length > 0 ||
            tabData.external_comment ||
            tabData.id
          ) {
            // Add PRODUCT SHEET as a single object
            const productSheetData = {
              ...processedFields,
              external_comment: tabData.external_comment || null
            };

            // Include id if present (for edit mode)
            if (tabData.id) {
              productSheetData.id = tabData.id;
            }

            formattedData[stream.key] = productSheetData;
          }
        } else {
          // Handle multiple instances - add as array
          const instances = [];
          this.getStreamInstances(stream.key).forEach(instance => {
            const processedFields = this.processFieldValues(instance.fields);
            if (
              Object.keys(processedFields).length > 0 ||
              instance.external_comment ||
              instance.db_id
            ) {
              const instanceData = {
                ...processedFields,
                external_comment: instance.external_comment || null
              };

              // Include db_id if present (for edit mode)
              if (instance.db_id) {
                instanceData.id = instance.db_id;
              }

              instances.push(instanceData);
            }
          });

          // Only add the stream if it has instances
          if (instances.length > 0) {
            formattedData[stream.key] = instances;
          }
        }
      });

      return formattedData;
    },

    processFieldValues(fields) {
      const processed = {};
      Object.entries(fields).forEach(([key, value]) => {
        if (value === "") {
          processed[key] = null;
        } else if (value?.name) {
          processed[key] = value.name;
        } else if (value instanceof Date) {
          processed[key] = dayjs(value).format("YYYY-MM-DD");
        } else {
          processed[key] = value;
        }
      });
      return processed;
    },

    async checkForDuplicates() {
      // Implementation to check for duplicates
      // This should call the backend API to check if product exists
      return { isDuplicate: false };
    },

    async submitProduct(productData) {
      try {
        this.$emit("save", productData);
        // Don't close the modal here - let the parent component handle closing it
        // after the backend API call succeeds
        this.isSaving = false;
      } catch (error) {
        this.handleError(error);
        this.isSaving = false;
      }
    },

    // Method that parent component can call to close the modal on successful save
    closeModalOnSuccess() {
      this.$buefy.toast.open({
        message: "Product saved successfully!",
        type: "is-success"
      });
      this.cancel();
    },

    // Method that parent component can call to show error after failed save
    showSaveError(errorMessage) {
      this.isSaving = false;
      this.$buefy.dialog.alert({
        title: "Error",
        message: errorMessage || "An error occurred while saving the product",
        type: "is-danger",
        ariaRole: "alertdialog",
        ariaModal: true
      });
    },

    handleError(error) {
      this.isSaving = false;
      const errorMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.error ||
        "An error occurred while saving the product";

      this.$buefy.dialog.alert({
        title: "Error",
        message: errorMessage,
        type: "is-danger",
        ariaRole: "alertdialog",
        ariaModal: true
      });
    },

    cancel() {
      this.isModalActive = false;
      this.tabFormData = {};
      this.streamInstances = {};
      this.validationErrors = {};
    },

    populateFormWithProductData(productResponse) {
      // Initialize form first to set up the structure (without default instances)
      this.initializeForm(false);

      // Populate PRODUCT SHEET
      if (productResponse["PRODUCT SHEET"]) {
        const productSheetData = productResponse["PRODUCT SHEET"];
        if (this.tabFormData["PRODUCT SHEET"]) {
          // Store the id in the main object (not in fields) for later use during save
          if (productSheetData.id) {
            this.tabFormData["PRODUCT SHEET"].id = productSheetData.id;
          }

          // Remove fields that shouldn't be in form
          const cleanData = { ...productSheetData };
          delete cleanData.id;
          delete cleanData.updated_by_name;

          // Set each field individually for proper Vue reactivity
          Object.keys(cleanData).forEach(fieldName => {
            const fieldValue = cleanData[fieldName];

            // Keep dropdown values as strings, don't convert to objects
            // The FormField component will handle matching them with options

            this.$set(
              this.tabFormData["PRODUCT SHEET"].fields,
              fieldName,
              fieldValue
            );
          });

          // Ensure flag fields (weee, batt, pack, bundle) are set for tab visibility
          // Always set them from the product data if they exist
          ["weee", "batt", "pack", "bundle"].forEach(flagField => {
            if (flagField in productSheetData) {
              this.$set(
                this.tabFormData["PRODUCT SHEET"].fields,
                flagField,
                productSheetData[flagField]
              );
            }
          });

          // Handle external_comment if exists
          if (productSheetData.external_comment) {
            this.$set(
              this.tabFormData["PRODUCT SHEET"],
              "external_comment",
              productSheetData.external_comment
            );
          }
        }
      }

      // Populate WEEE streams
      if (
        productResponse["WEEE"] &&
        Array.isArray(productResponse["WEEE"]) &&
        productResponse["WEEE"].length > 0
      ) {
        const weeInstances = [];
        productResponse["WEEE"].forEach(weeeData => {
          const instanceId = `WEEE_${++this.instanceCounter}`;
          const cleanData = {};

          // Copy only fields that should be included, excluding unwanted properties
          const fieldsToExclude = [
            "id",
            "stream_name",
            "stream_key",
            "product",
            "updated_by_name",
            "dc_applied",
            "data_comp_rules",
            "dc_created_at",
            "dc_error",
            "dc_by_name",
            "is_deleted"
          ];

          Object.keys(weeeData).forEach(key => {
            if (!fieldsToExclude.includes(key)) {
              cleanData[key] = weeeData[key];
            }
          });

          const instance = {
            id: instanceId,
            db_id: weeeData.id || null,
            stream_name: "WEEE",
            stream_key: "WEEE",
            external_comment: weeeData.external_comment || "",
            fields: cleanData
          };
          weeInstances.push(instance);
          this.$set(this.validationErrors, instanceId, {});
        });
        this.$set(this.streamInstances, "WEEE", weeInstances);
      }

      // Populate BATT streams
      if (
        productResponse["BATT"] &&
        Array.isArray(productResponse["BATT"]) &&
        productResponse["BATT"].length > 0
      ) {
        const battInstances = [];
        productResponse["BATT"].forEach(battData => {
          const instanceId = `BATT_${++this.instanceCounter}`;
          const cleanData = {};

          const fieldsToExclude = [
            "id",
            "stream_name",
            "stream_key",
            "product",
            "updated_by_name"
          ];

          Object.keys(battData).forEach(key => {
            if (!fieldsToExclude.includes(key)) {
              cleanData[key] = battData[key];
            }
          });

          const instance = {
            id: instanceId,
            db_id: battData.id || null,
            stream_name: "BATT",
            stream_key: "BATT",
            external_comment: battData.external_comment || "",
            fields: cleanData
          };
          battInstances.push(instance);
          this.$set(this.validationErrors, instanceId, {});
        });
        this.$set(this.streamInstances, "BATT", battInstances);
      }

      // Populate PACK streams
      if (
        productResponse["PACK"] &&
        Array.isArray(productResponse["PACK"]) &&
        productResponse["PACK"].length > 0
      ) {
        const packInstances = [];
        productResponse["PACK"].forEach(packData => {
          const instanceId = `PACK_${++this.instanceCounter}`;
          const cleanData = {};

          const fieldsToExclude = [
            "id",
            "stream_name",
            "stream_key",
            "product",
            "updated_by_name",
            "dc_applied",
            "data_comp_rules",
            "dc_created_at",
            "dc_error",
            "dc_by_name"
          ];

          Object.keys(packData).forEach(key => {
            if (!fieldsToExclude.includes(key)) {
              cleanData[key] = packData[key];
            }
          });

          const instance = {
            id: instanceId,
            db_id: packData.id || null,
            stream_name: "PACK",
            stream_key: "PACK",
            external_comment: packData.external_comment || "",
            fields: cleanData
          };
          packInstances.push(instance);
          this.$set(this.validationErrors, instanceId, {});
        });
        this.$set(this.streamInstances, "PACK", packInstances);
      }

      // Populate BUNDLE streams
      if (
        productResponse["BUNDLE"] &&
        Array.isArray(productResponse["BUNDLE"]) &&
        productResponse["BUNDLE"].length > 0
      ) {
        const bundleInstances = [];
        productResponse["BUNDLE"].forEach(bundleData => {
          const instanceId = `BUNDLE_${++this.instanceCounter}`;
          const cleanData = {};

          const fieldsToExclude = [
            "id",
            "stream_name",
            "stream_key",
            "product",
            "product_id",
            "updated_by_name"
          ];

          Object.keys(bundleData).forEach(key => {
            if (!fieldsToExclude.includes(key)) {
              cleanData[key] = bundleData[key];
            }
          });

          const instance = {
            id: instanceId,
            db_id: bundleData.id || null,
            stream_name: "BUNDLE INFORMATION",
            stream_key: "BUNDLE",
            external_comment: bundleData.external_comment || "",
            fields: cleanData
          };
          bundleInstances.push(instance);
          this.$set(this.validationErrors, instanceId, {});
        });
        this.$set(this.streamInstances, "BUNDLE", bundleInstances);
      }

      // Update title to indicate editing and trigger reactivity
      this.$nextTick(() => {
        const modalTitle = this.$el.querySelector(".modal-card-title");
        if (modalTitle) {
          modalTitle.textContent = "Edit Product";
        }
        // Trigger reactivity for visible streams computation
        this.reactivityTrigger++;
      });
    }
  }
};
</script>

<style scoped>
.is-wider-modal .modal-card {
  max-width: 1400px !important;
}

.scrollable-modal-body {
  max-height: 70vh;
  overflow-y: auto;
  position: relative;
  padding: 0;
}

.custom-tabs-header {
  position: sticky;
  top: 0;
  z-index: 11;
  background-color: white;
  padding: 10px 20px;
  border-bottom: 1px solid #dbdbdb;
  display: flex;
  justify-content: flex-end;
}

.header-action-buttons {
  display: flex;
  gap: 0.5rem;
}

.sticky-tabs-wrapper :deep(.tabs) {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: white;
  margin-bottom: 0;
  padding: 0 20px;
}
.no-margin-bottom {
  margin-bottom: 0.75rem;
}

.cdf-edit-add-padding {
  width: 100%;
}

.cdf-edit-select,
.cdf-edit-date-picker {
  width: 100%;
}

.modal-card-foot {
  justify-content: flex-start;
}

.modal-card-title {
  line-height: 28px;
}

.sub-tabs-inline :deep(.tabs) {
  position: sticky;
  top: 37.5px;
  z-index: 9;
  background-color: white;
}

.sub-tabs-inline :deep(.tabs ul) {
  border-bottom: none;
}
</style>
